import { ActivityIndicator, Dimensions, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Button, CachedImage, Text, colors, spacing, LoadingOverlay, PlusButton } from '@pletly/ui';
import { SelectedMedia } from '@pletly/common';
import { useBlurFaces } from './useBlurFaces';

interface Props {
	images: (SelectedMedia & { mediaIndex: number })[];
	defaultReferenceImageUrl?: string;
	blurredImages: Parameters<typeof useBlurFaces>['0']['blurredImages'];
	setBlurredImages: Parameters<typeof useBlurFaces>['0']['setBlurredImages'];
}

const REFERENCE_IMAGE_BUTTON_SIZE = 80;

export const BlurFaces = ({
	images,
	defaultReferenceImageUrl,
	blurredImages,
	setBlurredImages,
}: Props) => {
	const { t } = useTranslation();
	const {
		currentImage,
		referenceImage,
		onAddReferencePhotoPress,
		blurCurrentPhotoFaces,
		blurFacesOnAllPhotos,
		loading,
		isLoadingModels,
		goToPreviousImage,
		goToNextImage,
		isNext,
		isPrevious,
		undoCurrentBlur,
	} = useBlurFaces({
		images,
		defaultReferenceImageUrl,
		blurredImages,
		setBlurredImages,
	});

	if (isLoadingModels) {
		return <LoadingOverlay show />;
	}

	return (
		<View style={styles.root}>
			<View style={styles.section}>
				<View>
					<Text category="h4">{t('post.new.blur-faces.care-receiver-reference-photo')}</Text>
					<Text category="p1">
						{t(
							'post.new.blur-faces.add-a-photo-of-the-care-recipients-face-that-is-not-to-be-blured-out',
						)}
					</Text>
				</View>

				{referenceImage ? (
					<Pressable onPress={onAddReferencePhotoPress} disabled={loading}>
						<CachedImage
							source={{
								uri:
									referenceImage instanceof HTMLImageElement
										? referenceImage.src
										: referenceImage.uri,
							}}
							style={styles.referenceImage}
							blurRadius={loading ? 4 : 0}
						/>
					</Pressable>
				) : (
					<PlusButton
						testID="add-reference-photo-button"
						size={REFERENCE_IMAGE_BUTTON_SIZE}
						onPress={onAddReferencePhotoPress}
					/>
				)}
			</View>

			<View style={styles.section}>
				<View style={styles.title}>
					<Text category="h4">
						{t('post.new.blur-faces.blur-faces-in-photos', { count: images.length })}
					</Text>

					{referenceImage && (
						<Button
							variant="outline"
							size="small"
							color="primary"
							disabled={loading}
							onPress={blurFacesOnAllPhotos}
						>
							{t('post.new.blur-faces.blur-all')}
						</Button>
					)}
				</View>

				<View style={styles.imageContainer}>
					<CachedImage
						source={{
							uri: currentImage.uri,
						}}
						contentFit="contain"
						style={styles.image}
						blurRadius={loading ? 12 : 0}
					/>

					<View style={styles.controls}>
						{images.length > 1 && (
							<Button
								variant="filled"
								size="small"
								onPress={goToPreviousImage}
								disabled={loading || !isPrevious}
								style={isPrevious ? undefined : styles.hidden}
							>
								{t('post.new.blur-faces.previous-photo')}
							</Button>
						)}

						<Button
							color={currentImage.isBlurred ? 'danger' : 'primary'}
							variant={currentImage.isBlurred ? 'outline' : 'filled'}
							onPress={currentImage.isBlurred ? undoCurrentBlur : blurCurrentPhotoFaces}
							size="small"
							style={styles.blurButton}
							loading={loading}
							disabled={!referenceImage}
						>
							{currentImage.isBlurred ? t('undo') : t('post.blur-faces')}
						</Button>

						{images.length > 1 && (
							<Button
								variant="filled"
								size="small"
								onPress={goToNextImage}
								disabled={loading || !isNext}
								style={isNext ? undefined : styles.hidden}
							>
								{t('post.new.blur-faces.next-photo')}
							</Button>
						)}
					</View>
				</View>

				<Text status="warning" category="c1">
					{t('summarization.warning-text')}
				</Text>
				{loading && <ActivityIndicator size="large" style={styles.loadingSpinner} />}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	root: { gap: spacing(3) },
	section: { gap: spacing(2) },
	title: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	referenceImage: { width: REFERENCE_IMAGE_BUTTON_SIZE, height: REFERENCE_IMAGE_BUTTON_SIZE },
	imageContainer: {
		borderRadius: 4,
		flex: 1,
		minHeight: Dimensions.get('window').height - 460,
		alignItems: 'center',
		justifyContent: 'center',
	},
	image: { borderRadius: 4, width: '100%', flex: 1 },
	loadingSpinner: { position: 'absolute', left: '50%', top: '50%' },
	controls: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: spacing(2),
		borderRadius: spacing(2),
		backgroundColor: colors.neutral200,
		position: 'absolute',
		bottom: 15.5,
		width: `calc(100% - ${2 * 12}px)`,
		marginHorizontal: 12,
	},
	blurButton: { margin: 'auto' },
	hidden: {
		opacity: 0,
	},
});
